import { useRef } from 'react'

import { PostTypeGhostPost } from '~publish/legacy/post/constants'

import { ThreadGhostPost } from './components/ThreadGhostPost'
import { ThreadPost } from './components/ThreadPost'
import { Empty } from '../Common/Empty'
import { getPostPreviewEntries } from '../Common/getPostPreviewEntries'
import { useScrollToThreadedPost } from '../Common/hooks/useScrollToThreadedPost'
import { PostPreviewCard } from '../Common/styles'
import type { ProfilePreview, ThreadsDraft, ThreadType } from '../types'

import type { JSX } from 'react'

const getPosts = (draft: ThreadsDraft): ThreadType[] => {
  if (draft.thread) {
    const { activeThreadId } = draft

    return draft.thread.map((threadedDraft, i) => ({
      content:
        activeThreadId === i
          ? {
              text: draft.text,
              enabledAttachmentType: draft.enabledAttachmentType,
              images: draft.images,
              video: draft.video,
              videos: draft.videos,
              gif: draft.gif,
              link: draft.link,
              assets: draft.assets,
              inThread: true,
              channelData: draft.channelData,
              scheduledAt: draft.scheduledAt,
            }
          : {
              ...threadedDraft,
              inThread: true,
              channelData: draft.channelData,
              scheduledAt: draft.scheduledAt,
            },
    }))
  } else {
    return [
      {
        content: {
          ...draft,
          text: draft.text,
          inThread: false,
          channelData: draft.channelData,
        },
      },
    ]
  }
}

export const ThreadsPreview = ({
  draft,
  profile,
}: {
  draft: ThreadsDraft
  profile: ProfilePreview
}): JSX.Element => {
  const { isEmpty, hasThread, activeThreadId, service, images } = draft
  const singlePostIdentityRef = useRef<object>({})
  const posts = getPosts(draft)
  const postEntries = getPostPreviewEntries(
    posts,
    (post) => post.content,
    (_post, index) => draft.thread?.[index] ?? singlePostIdentityRef.current,
  )

  // Disabled scroll to thread when in omni mode
  useScrollToThreadedPost(!service.isOmni ? activeThreadId : undefined)

  // In omni mode the draft can have gifs, but they're not supported so we don't render then
  const hasGif = !!images?.length && images[0]?.mediaType === 'gif'
  const allThreadsEmpty = posts?.every((post) => !post.content.text)

  // Only hide preview if all threads are empty
  if (isEmpty && allThreadsEmpty && !hasThread && !hasGif) {
    return <Empty />
  }

  if (draft.updateType === PostTypeGhostPost) {
    return (
      <PostPreviewCard data-testid="threads-preview">
        <ThreadGhostPost
          content={{
            ...draft,
            text: draft.text,
            inThread: false,
            channelData: draft.channelData,
          }}
          profile={profile}
        />
      </PostPreviewCard>
    )
  }

  return (
    <PostPreviewCard data-testid="threads-preview">
      {postEntries.map(({ key, post }, index) => (
        <ThreadPost
          key={key}
          content={post.content}
          profile={profile}
          index={index}
        />
      ))}
    </PostPreviewCard>
  )
}
